/**
 * Design tokens, and how an org customises them.
 *
 * The catalogue below repeats the canonical values of `src/styles/tokens.css`
 * on purpose: the screen offers "back to the original value" without shipping
 * the whole stylesheet, and `tokens.test.ts` checks on every run that it has
 * not drifted from it.
 *
 * A colour is a bare HSL triplet, "239 84% 60%": that is what
 * `hsl(var(--primary) / .1)` expects, and it lets a token's opacity vary
 * without defining a variant.
 *
 * The saved setting `design.tokens` is shared with the React app (same org
 * setting). Its shape is `{ clair?: {...}, sombre?: {...} }`: the theme keys
 * stay French because the React app and the API (analytics/reportPack.ts)
 * read them. Token keys are CSS variable names without the two dashes.
 */

/** Theme keys of the saved setting (shared with the React app and the API). */
export type SavedTheme = 'clair' | 'sombre'
/** Values per token for one theme. Only the tokens that CHANGED are present. */
export type Tokens = Record<string, string>
export type TokensByTheme = Partial<Record<SavedTheme, Tokens>>

export const SETTING_KEY = 'design.tokens'

export interface TokenDef {
  /** CSS variable name, without the two dashes. */
  key: string
  /** Has a help text (`design.token.<id>.help`). A colour with no known use is not offered. */
  help?: boolean
  /** A light swatch on a light background is invisible without an outline. */
  outline?: boolean
  clair: string
  sombre: string
}

export interface TokenGroup {
  /** i18n id: `design.group.<id>.title` / `.sub`. */
  id: string
  tokens: TokenDef[]
}

export const GROUPS: TokenGroup[] = [
  {
    id: 'surfaces',
    tokens: [
      { key: 'background', outline: true, help: true, clair: '220 20% 99%', sombre: '224 32% 6%' },
      { key: 'foreground', help: true, clair: '222 47% 11%', sombre: '210 20% 96%' },
      { key: 'card', outline: true, help: true, clair: '0 0% 100%', sombre: '224 28% 9%' },
      { key: 'muted', outline: true, help: true, clair: '220 14% 96%', sombre: '223 22% 14%' },
      { key: 'muted-foreground', help: true, clair: '220 10% 40%', sombre: '220 12% 72%' },
      { key: 'subtle', outline: true, help: true, clair: '220 14% 93%', sombre: '223 22% 12%' },
      { key: 'border', outline: true, help: true, clair: '220 13% 91%', sombre: '222 22% 18%' },
      { key: 'border-strong', outline: true, help: true, clair: '220 13% 84%', sombre: '222 22% 26%' },
      { key: 'input', outline: true, help: true, clair: '220 13% 88%', sombre: '222 22% 20%' },
      { key: 'ring', help: true, clair: '239 84% 60%', sombre: '239 84% 65%' },
    ],
  },
  {
    id: 'brand',
    tokens: [
      { key: 'primary', help: true, clair: '239 84% 60%', sombre: '239 84% 65%' },
      { key: 'primary-foreground', outline: true, help: true, clair: '0 0% 100%', sombre: '222 47% 8%' },
      { key: 'primary-hover', help: true, clair: '239 80% 54%', sombre: '239 84% 72%' },
      { key: 'success', help: true, clair: '152 60% 38%', sombre: '152 55% 45%' },
      { key: 'warning', help: true, clair: '35 92% 50%', sombre: '35 90% 58%' },
      { key: 'danger', help: true, clair: '0 75% 55%', sombre: '0 75% 62%' },
      { key: 'info', help: true, clair: '217 91% 55%', sombre: '217 91% 62%' },
    ],
  },
  {
    id: 'agent',
    tokens: [
      { key: 'agent-online', clair: '152 60% 38%', sombre: '152 55% 50%' },
      { key: 'agent-incall', clair: '217 91% 55%', sombre: '217 91% 62%' },
      { key: 'agent-pause', clair: '35 92% 50%', sombre: '35 90% 58%' },
      { key: 'agent-acw', clair: '270 65% 58%', sombre: '270 70% 68%' },
      { key: 'agent-offline', clair: '220 9% 55%', sombre: '220 10% 50%' },
    ],
  },
  {
    id: 'charts',
    tokens: [
      { key: 'chart-1', clair: '239 84% 60%', sombre: '239 84% 68%' },
      { key: 'chart-2', clair: '152 60% 42%', sombre: '152 55% 50%' },
      { key: 'chart-3', clair: '35 92% 55%', sombre: '35 90% 60%' },
      { key: 'chart-4', clair: '0 75% 60%', sombre: '0 75% 65%' },
      { key: 'chart-5', clair: '270 65% 60%', sombre: '270 70% 68%' },
      { key: 'chart-6', clair: '217 91% 55%', sombre: '217 91% 65%' },
    ],
  },
]

export const ALL_TOKENS: TokenDef[] = GROUPS.flatMap((g) => g.tokens)

/** Border radii. Not colours: their value is a length. Saved in the light theme only (like the React app). */
export const RADII = [
  { key: 'radius-card', default: '8px' },
  { key: 'radius-input', default: '6px' },
] as const

/** i18n id of a token or radius: `muted-foreground` → `mutedForeground`, `chart-1` → `chart1`. */
export const i18nId = (key: string): string => key.replace(/-(\w)/g, (_, c: string) => c.toUpperCase())

/* ----- names shared with the React app ----------------------------------- */

/**
 * Mapping between the two apps' token names.
 *
 * The React app's editor (web-react-legacy lib/jetons.ts) edits and saves the
 * same design-layer names as this one: `background`, `primary`, `success`,
 * `radius-card`… Its short historical names (`--acc`, `--ok`, `--line`…) are
 * NOT separate tokens: its tokens.css derives them from the design-layer ones
 * (`--acc: hsl(var(--primary))`), so a saved `primary` recolours both apps.
 *
 * - `VUE_TO_REACT`: the name the React app reads for a Vue token, when it
 *   differs. Every editable token currently has the same name in both apps,
 *   so the table is empty and a value is written once; a rename on either
 *   side goes here, and saving then writes both names.
 * - `LEGACY_ALIASES`: React-only historical names → Vue name. Read when
 *   loading, so a value stored under one of them still applies here; never
 *   written, because the React app expects a full colour (`hsl(...)`) in
 *   those variables, not a bare triplet, and would break.
 */
export const VUE_TO_REACT: Record<string, string> = {}

export const LEGACY_ALIASES: Record<string, string> = {
  bg: 'background',
  surface: 'card',
  'surface-2': 'muted',
  fg: 'foreground',
  mut: 'muted-foreground',
  line: 'border',
  'line-strong': 'border-strong',
  acc: 'primary',
  'acc-fg': 'primary-foreground',
  ok: 'success',
  'ok-soft': 'success-soft',
  'ok-fg': 'success-soft-fg',
  warn: 'warning',
  'warn-soft': 'warning-soft',
  'warn-fg': 'warning-soft-fg',
  ko: 'danger',
  'ko-soft': 'danger-soft',
  'ko-fg': 'danger-soft-fg',
  'info-c': 'info',
  'info-soft-bg': 'info-soft',
  'info-fg': 'info-soft-fg',
  r: 'radius-card',
  'r-sm': 'radius-input',
}

/** Names a Vue token is saved under: its own, plus the React one when it differs. */
export function savedNamesOf(vueKey: string): string[] {
  const react = VUE_TO_REACT[vueKey]
  return react && react !== vueKey ? [vueKey, react] : [vueKey]
}

const REACT_TO_VUE: Record<string, string> = {
  ...LEGACY_ALIASES,
  ...Object.fromEntries(Object.entries(VUE_TO_REACT).map(([vue, react]) => [react, vue])),
}

/** A colour stored as `hsl(<triplet>)` or `#rrggbb` becomes a bare triplet; anything else is kept. */
function asTriplet(value: string): string {
  const fn = /^\s*hsl\(\s*([\d.]+\s+[\d.]+%\s+[\d.]+%)\s*\)\s*$/i.exec(value)
  if (fn) return fn[1]!
  if (/^\s*#[\da-f]{6}\s*$/i.test(value)) return hexToHsl(value)
  return value
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)

/**
 * The saved setting, as Vue token names. A key saved under a React name is
 * applied to its Vue name; when both are present, the Vue name wins (it is
 * what this app wrote last). Non-string values are dropped.
 */
export function fromSaved(value: unknown): TokensByTheme {
  if (!isRecord(value)) return {}
  const out: TokensByTheme = {}
  for (const theme of ['clair', 'sombre'] as const) {
    const saved = value[theme]
    if (!isRecord(saved)) continue
    const tokens: Tokens = {}
    for (const [key, v] of Object.entries(saved)) {
      if (typeof v !== 'string') continue
      const vue = REACT_TO_VUE[key]
      if (vue) {
        if (!(vue in saved)) tokens[vue] = key in LEGACY_ALIASES ? asTriplet(v) : v
      } else {
        tokens[key] = v
      }
    }
    out[theme] = tokens
  }
  return out
}

/** Vue-named tokens, as the setting to save: each value under every name that must carry it. */
export function toSaved(tokens: TokensByTheme): TokensByTheme {
  const out: TokensByTheme = {}
  for (const theme of ['clair', 'sombre'] as const) {
    const t = tokens[theme]
    if (!t) continue
    const saved: Tokens = {}
    for (const [key, v] of Object.entries(t)) for (const name of savedNamesOf(key)) saved[name] = v
    out[theme] = saved
  }
  return out
}

/* ----- conversions -------------------------------------------------------- */

/**
 * HSL triplet to hexadecimal, for an `<input type="color">`. An unreadable
 * value gives black instead of throwing: the screen must show a mistyped
 * token, not disappear.
 */
export function hslToHex(triplet: string): string {
  const m = /^\s*([\d.]+)\s+([\d.]+)%\s+([\d.]+)%\s*$/.exec(triplet)
  if (!m) return '#000000'
  const h = Number(m[1]) / 360, s = Number(m[2]) / 100, l = Number(m[3]) / 100
  const f = (n: number) => {
    const k = (n + h * 12) % 12
    const a = s * Math.min(l, 1 - l)
    const v = l - a * Math.max(-1, Math.min(k - 3, Math.min(9 - k, 1)))
    return Math.round(255 * v).toString(16).padStart(2, '0')
  }
  return `#${f(0)}${f(8)}${f(4)}`
}

/** Hexadecimal to HSL triplet, rounded the way tokens.css writes them. */
export function hexToHsl(hex: string): string {
  const m = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex.trim())
  if (!m) return '0 0% 0%'
  const [r, g, b] = [m[1]!, m[2]!, m[3]!].map((x) => parseInt(x, 16) / 255) as [number, number, number]
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  const l = (max + min) / 2
  const d = max - min
  let h = 0, s = 0
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1))
    h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4
    h *= 60
    if (h < 0) h += 360
  }
  const round = (n: number) => Math.round(n * 10) / 10
  return `${round(h)} ${round(s * 100)}% ${round(l * 100)}%`
}

/* ----- applying ----------------------------------------------------------- */

/**
 * Stylesheet of the customised tokens. Same structure as tokens.css: a light
 * block, a dark block for the system setting, a dark block for the explicit
 * choice. Light values in a single `:root` would also win in dark mode, where
 * they would be unreadable. Empty when nothing is customised.
 */
export function tokensCss(custom: TokensByTheme): string {
  const block = (t: Tokens | undefined) => Object.entries(t ?? {}).map(([k, v]) => `--${k}:${v};`).join('')
  const light = block(custom.clair)
  const dark = block(custom.sombre)
  const parts: string[] = []
  if (light) parts.push(`:root{${light}}`)
  if (dark) {
    parts.push(`@media (prefers-color-scheme:dark){:root:not([data-theme="light"]){${dark}}}`)
    parts.push(`:root[data-theme="dark"]{${dark}}`)
  }
  return parts.join('\n')
}

const STYLE_ID = 'org-design-tokens'

/** Puts (or removes) the stylesheet of the customised tokens. */
export function applyTokens(custom: TokensByTheme): void {
  const css = tokensCss(custom)
  let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null
  if (!css) { el?.remove(); return }
  if (!el) {
    el = document.createElement('style')
    el.id = STYLE_ID
    document.head.append(el)
  }
  el.textContent = css
}

/**
 * The whole stylesheet to export. EVERY token is written, not only the
 * changed ones: the file must replace the repository's `:root` block as is.
 * `header` is the comment's text (translated and dated by the caller).
 */
export function exportTokensCss(custom: TokensByTheme, header: string[]): string {
  const val = (t: TokenDef, theme: SavedTheme) => custom[theme]?.[t.key] ?? t[theme]
  const lines = (theme: SavedTheme) => ALL_TOKENS.map((t) => `  --${t.key}: ${val(t, theme)};`).join('\n')
  const radii = RADII.map((r) => `  --${r.key}: ${custom.clair?.[r.key] ?? r.default};`).join('\n')
  return `/* ${header.join('\n   ')} */
:root {
${lines('clair')}
${radii}
}

@media (prefers-color-scheme: dark) {
  :root:not([data-theme="light"]) {
${lines('sombre')}
  }
}

:root[data-theme="dark"] {
${lines('sombre')}
}
`
}

/* ----- the editor's draft ------------------------------------------------- */

/** What is saved, plus what is being tried. */
export function mergeDraft(saved: TokensByTheme, draft: TokensByTheme): TokensByTheme {
  return { clair: { ...saved.clair, ...draft.clair }, sombre: { ...saved.sombre, ...draft.sombre } }
}

export const changeCount = (draft: TokensByTheme): number =>
  Object.keys(draft.clair ?? {}).length + Object.keys(draft.sombre ?? {}).length

export function withToken(t: TokensByTheme, theme: SavedTheme, key: string, value: string): TokensByTheme {
  return { ...t, [theme]: { ...t[theme], [key]: value } }
}

export function withoutToken(t: TokensByTheme, theme: SavedTheme, key: string): TokensByTheme {
  const rest = { ...t[theme] }
  delete rest[key]
  return { ...t, [theme]: rest }
}
